(() => {
  if (customElements.get('rosarium-occasion-popout')) return;
  class OccasionPopout extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const on = (node, name, fn, options = {}) => node.addEventListener(name, fn, { ...options, signal: this.abort.signal });
      const rail = this.querySelector('.occasion-popout__rail');
      const previous = this.querySelector('[data-occasion-previous]');
      const next = this.querySelector('[data-occasion-next]');
      const controls = this.querySelector('.occasion-popout__controls');
      const progress = this.querySelector('.occasion-popout__progress');
      const thumb = progress.firstElementChild;
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      const behaviour = () => motion.matches ? 'auto' : 'smooth';
      const step = () => rail.firstElementChild.getBoundingClientRect().width + parseFloat(getComputedStyle(rail).columnGap);
      const move = direction => rail.scrollBy({ left: step() * direction, behavior: behaviour() });
      const update = () => {
        const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
        const position = Math.max(0, Math.min(max, rail.scrollLeft));
        previous.disabled = position <= 2;
        next.disabled = max - position <= 2;
        controls.hidden = progress.hidden = max <= 2;
        const size = Math.max(18, Math.min(100, rail.clientWidth / rail.scrollWidth * 100));
        thumb.style.width = `${size}%`;
        thumb.style.transform = `translateX(${max ? position / max * (100 - size) / (size / 100) : 0}%)`;
      };
      const queue = () => { cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(update); };
      on(rail, 'scroll', queue, { passive: true });
      on(previous, 'click', () => move(-1));
      on(next, 'click', () => move(1));
      on(rail, 'keydown', event => {
        if (event.target !== rail) return;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') rail.scrollTo({ left: event.key === 'Home' ? 0 : rail.scrollWidth, behavior: behaviour() });
        else move(event.key === 'ArrowLeft' ? -1 : 1);
      });
      on(rail, 'focusin', event => {
        const item = event.target.closest('li');
        if (!item) return;
        const r = rail.getBoundingClientRect(), i = item.getBoundingClientRect();
        const inset = parseFloat(getComputedStyle(rail).paddingLeft);
        if (i.left < r.left + inset || i.right > r.right - inset) rail.scrollBy({ left: i.left - r.left - inset, behavior: 'auto' });
      });
      on(this, 'shopify:block:select', event => event.target.closest('li')?.querySelector('a')?.focus({ preventScroll: true }));
      on(document, 'shopify:section:unload', event => { if (event.target.contains(this)) this.disconnectedCallback(); });
      on(document, 'shopify:section:load', event => { if (event.target.contains(this)) this.connectedCallback(); });
      this.observer = new ResizeObserver(queue);
      this.observer.observe(rail);
      this.cleanupDrag = this.enableMouseDrag(rail);
      update();
    }
    disconnectedCallback() {
      this.cleanupDrag?.();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.abort?.abort();
      this.abort = null;
    }
    enableMouseDrag(rail) {
      const abort = new AbortController();
      const on = (node, name, handler, options = {}) => node.addEventListener(name, handler, { ...options, signal: abort.signal });
      let press = null, suppressClick = false;
      const clearClick = () => { suppressClick = false; };
      const finish = () => {
        if (!press) return;
        const { id, dragging } = press;
        press = null;
        rail.classList.remove('is-mouse-dragging');
        if (rail.hasPointerCapture(id)) rail.releasePointerCapture(id);
        if (dragging) suppressClick = true;
      };
      rail.classList.add('has-mouse-drag');
      on(rail, 'pointerdown', event => {
        clearClick();
        if (event.pointerType !== 'mouse' || event.button !== 0 || !event.isPrimary ||
            event.ctrlKey || event.metaKey || event.altKey || event.shiftKey ||
            rail.scrollWidth <= rail.clientWidth + 1 ||
            event.target.closest('button, input, select, textarea, [contenteditable="true"]')) return;
        finish();
        clearClick();
        press = { id: event.pointerId, x: event.clientX, y: event.clientY, left: rail.scrollLeft, dragging: false };
      });
      on(window, 'pointermove', event => {
        if (!press || event.pointerId !== press.id) return;
        if (!(event.buttons & 1)) { finish(); return; }
        const dx = event.clientX - press.x, dy = event.clientY - press.y;
        if (!press.dragging) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) < 6) return;
          if (Math.abs(dy) >= Math.abs(dx)) { finish(); return; }
          press.dragging = true;
          rail.classList.add('is-mouse-dragging');
          rail.setPointerCapture(press.id);
        }
        event.preventDefault();
        rail.scrollLeft = press.left - dx;
      }, { passive: false });
      on(window, 'pointerup', event => { if (event.pointerId === press?.id) finish(); });
      on(window, 'pointercancel', event => { if (event.pointerId === press?.id) finish(); });
      on(rail, 'lostpointercapture', finish);
      on(window, 'blur', finish);
      on(window, 'keydown', event => { if (event.key === 'Escape') finish(); });
      on(rail, 'dragstart', event => event.preventDefault());
      on(rail, 'click', event => {
        if (!suppressClick || event.detail === 0) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        clearClick();
      }, { capture: true });
      return () => { abort.abort(); finish(); clearClick(); rail.classList.remove('has-mouse-drag'); };
    }
  }
  customElements.define('rosarium-occasion-popout', OccasionPopout);
})();
